// PushNotificationButton — enables browser push for the current admin.
// Registered per-role (default 'housekeeping'); server broadcasts to that role
// when a room is checked out.
import React, { useEffect, useState } from 'react';
import { Bell, BellOff, Check, Loader2 } from 'lucide-react';
import { apiCall, API_URL, getToken } from './_shared';

const T = {
  en: {
    enable: 'Enable alerts', enabled: 'Alerts on', off: 'Alerts off',
    unsupported: 'Push not supported', test: 'Test',
    denied: 'Enable notifications in browser settings to receive alerts.',
  },
  me: {
    enable: 'Uključi obavještenja', enabled: 'Obavještenja aktivna', off: 'Obavještenja isključena',
    unsupported: 'Push nije podržan', test: 'Test',
    denied: 'Uključite obavještenja u postavkama preglednika.',
  },
  sq: {
    enable: 'Aktivizo njoftimet', enabled: 'Njoftimet aktive', off: 'Njoftimet joaktive',
    unsupported: 'Push i pambështetur', test: 'Test',
    denied: 'Aktivizoni njoftimet nga cilësimet e shfletuesit.',
  },
};

// Convert base64url VAPID public key → Uint8Array for PushManager.subscribe().
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

const PushNotificationButton = ({ role = 'housekeeping', lang = 'en' }) => {
  const t = T[lang] || T.en;
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [publicKey, setPublicKey] = useState('');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const s = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setSupported(s);
    if (!s) return;
    setPermission(Notification.permission);
    // Pull VAPID public key + current subscription state.
    (async () => {
      try {
        const { public_key } = await apiCall('/api/admin/push/vapid-public-key');
        setPublicKey(public_key);
      } catch {}
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
      } catch {}
    })();
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      // Ensure permission.
      const perm = Notification.permission === 'default'
        ? await Notification.requestPermission()
        : Notification.permission;
      setPermission(perm);
      if (perm !== 'granted') { alert(t.denied); return; }

      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        if (!publicKey) throw new Error('Missing VAPID key');
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }
      const json = sub.toJSON();
      await apiCall('/api/admin/push/subscribe', {
        method: 'POST',
        body: JSON.stringify({
          subscription: { endpoint: json.endpoint, keys: json.keys },
          role,
        }),
      });
      setSubscribed(true);
    } catch (e) {
      alert('Failed to enable: ' + e.message);
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await apiCall('/api/admin/push/unsubscribe', {
          method: 'POST',
          body: JSON.stringify({ endpoint: sub.endpoint }),
        });
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } catch (e) {
      alert('Failed to disable: ' + e.message);
    } finally {
      setBusy(false);
    }
  };

  const testPush = async () => {
    setBusy(true);
    try {
      await apiCall('/api/admin/push/test', { method: 'POST' });
    } catch (e) {
      alert('Test failed: ' + e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return (
      <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-500 px-2.5 py-1.5 rounded text-xs" data-testid="push-unsupported">
        <BellOff className="w-3.5 h-3.5" /> {t.unsupported}
      </span>
    );
  }

  if (subscribed) {
    return (
      <div className="inline-flex items-center gap-1" data-testid="push-enabled">
        <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 border border-green-200 px-2.5 py-1.5 rounded text-xs font-semibold">
          <Check className="w-3.5 h-3.5" /> {t.enabled}
        </span>
        <button
          onClick={testPush}
          disabled={busy}
          className="text-[11px] text-orange-600 hover:text-orange-700 px-1.5 py-1 disabled:opacity-50"
          data-testid="push-test"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : t.test}
        </button>
        <button
          onClick={disable}
          disabled={busy}
          className="text-[11px] text-gray-500 hover:text-red-600 px-1.5 py-1 disabled:opacity-50"
          data-testid="push-disable"
        >
          <BellOff className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={enable}
      disabled={busy}
      className="inline-flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded text-xs font-semibold shadow disabled:opacity-50"
      data-testid="push-enable-btn"
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />} {t.enable}
    </button>
  );
};

export default PushNotificationButton;
